import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, ShieldAlert, Trophy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "@/components/ui/sonner";
import QuizRulesModal from "@/components/QuizRulesModal";
import { playSelect, playNext, playTick, playWarn, playComplete, playUnlock } from "@/lib/sounds";

const LETTERS = ["A", "B", "C", "D"];

export default function Quiz() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [attemptId, setAttemptId] = useState(null);
  const [category, setCategory] = useState(null);
  const [q, setQ] = useState(null);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(false);
  const [tabWarnings, setTabWarnings] = useState(0);
  const [started, setStarted] = useState(false);
  const [remainPct, setRemainPct] = useState(100);
  const [remainSec, setRemainSec] = useState(0);
  const deadlineRef = useRef(null);
  const lastTickRef = useRef(null);
  const timerSecRef = useRef(20);
  const submittingRef = useRef(false);

  const reportEvent = useCallback((type, meta = {}) => {
    if (!attemptId) return;
    api.post("/quiz/anticheat", { type, attempt_id: attemptId, category_id: categoryId, question_id: q?.question_id || "", meta })
      .then(({ data }) => { if (typeof data.tab_events === "number") setTabWarnings(data.tab_events); })
      .catch(() => {});
  }, [attemptId, categoryId, q]);

  // Anti-cheat listeners
  useEffect(() => {
    const onVis = () => {
      reportEvent(document.hidden ? "tab_hidden" : "tab_visible");
      if (document.hidden) playWarn();
    };
    const onBlur = () => reportEvent("tab_blur");
    const onFocus = () => reportEvent("tab_focus");
    const onCtx = (e) => { e.preventDefault(); reportEvent("context_menu"); };
    const onCopy = (e) => { e.preventDefault(); reportEvent("copy_attempt"); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "u", "s"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        reportEvent("copy_attempt", { key: e.key });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("keydown", onKey);
    };
  }, [reportEvent]);

  const applyQuestion = useCallback((payload) => {
    playNext();
    lastTickRef.current = null;
    setQ(payload);
    setSelected(null);
    setLocked(false);
    deadlineRef.current = new Date(payload.deadline).getTime();
    timerSecRef.current = payload.timer;
  }, []);

  const submitAnswer = useCallback(async (optIndex, isTimeout = false) => {
    if (submittingRef.current || !attemptId || !q) return;
    submittingRef.current = true;
    setLocked(true);
    try {
      const { data } = await api.post(`/quiz/attempt/${attemptId}/answer`, {
        question_id: q.question_id,
        option_index: isTimeout ? null : optIndex,
      });
      setScore(data.score);
      if (data.done) {
        playComplete();
        setDone(true);
      } else if (data.question) {
        setTimeout(() => applyQuestion(data.question), 350);
      }
    } catch (e) {
      const msg = apiError(e);
      if (e?.response?.status === 409) {
        try {
          const { data } = await api.get(`/quiz/attempt/${attemptId}/current`);
          if (data.done) setDone(true);
          else applyQuestion(data.question);
        } catch { setError(msg); }
      } else if (e?.response?.status === 400 && /finished/i.test(msg)) {
        setDone(true);
      } else {
        setError(msg);
      }
    } finally {
      submittingRef.current = false;
    }
  }, [attemptId, q, applyQuestion]);

  // countdown ticker (display only; server is authoritative)
  useEffect(() => {
    const t = setInterval(() => {
      if (!deadlineRef.current || done) return;
      const remain = deadlineRef.current - Date.now();
      const total = timerSecRef.current * 1000;
      setRemainPct(Math.max(0, Math.min(100, (remain / total) * 100)));
      const sec = Math.max(0, Math.ceil(remain / 1000));
      setRemainSec(sec);
      if (sec <= 5 && sec > 0 && lastTickRef.current !== sec) {
        lastTickRef.current = sec;
        playTick();
      }
      if (remain <= -1500 && !submittingRef.current) submitAnswer(null, true);
    }, 100);
    return () => clearInterval(t);
  }, [done, submitAnswer]);

  // start / resume attempt
  useEffect(() => {
    if (user === null) { navigate("/auth"); return; }
    if (!user || !started) return;
    (async () => {
      try {
        const { data } = await api.post(`/quiz/category/${categoryId}/start`);
        setAttemptId(data.attempt_id);
        setCategory(data.category);
        if (data.done) setDone(true);
        else applyQuestion(data.question);
      } catch (e) {
        setError(apiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, categoryId, started]);

  // online/offline indicator
  useEffect(() => {
    const off = () => { setOffline(true); toast.error("Connection interrupted — reconnecting…"); };
    const on = () => { setOffline(false); toast.success("Connection restored."); };
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    return () => { window.removeEventListener("offline", off); window.removeEventListener("online", on); };
  }, []);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <div data-testid="quiz-error" className="text-lg font-heading font-bold text-slate-800">{error}</div>
        <button onClick={() => navigate("/dashboard")} data-testid="quiz-back-btn" className="mt-6 px-6 py-3 rounded-lg bg-[#003366] text-white font-bold">Back to Dashboard</button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="quiz-completed">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <div className="w-20 h-20 mx-auto rounded-full bg-[#FF9933]/10 flex items-center justify-center mb-6">
            <Trophy className="w-10 h-10 text-[#FF9933]" />
          </div>
          <h1 className="text-4xl font-black font-heading tracking-tighter text-slate-900">Category Complete! 🇮🇳</h1>
          <p className="text-slate-500 mt-2">{category?.name || "Category"} finished. Score from this attempt:</p>
          <div className="font-timer text-6xl font-bold text-[#FF9933] mt-4" data-testid="quiz-final-score">{score}</div>
          <div className="mt-8 flex justify-center gap-4">
            <button onClick={() => navigate("/dashboard")} data-testid="quiz-done-dashboard-btn" className="px-6 py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Dashboard</button>
            <button onClick={() => navigate("/leaderboard")} data-testid="quiz-done-leaderboard-btn" className="px-6 py-3 rounded-lg border-2 border-[#138808] text-[#138808] font-bold hover:bg-[#138808] hover:text-white transition-colors">Leaderboard</button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center text-slate-400 font-semibold">
        Preparing your category…
        <QuizRulesModal open={!started} onAccept={() => { playUnlock(); setStarted(true); }} onCancel={() => navigate("/dashboard")} />
      </div>
    );
  }

  const timerColor = remainPct > 50 ? "#FF9933" : remainPct > 25 ? "#E67E22" : "#DC2626";

  return (
    <div className="quiz-no-copy min-h-[80vh]" data-testid="quiz-interface">
      {offline && (
        <div data-testid="offline-banner" className="bg-red-600 text-white text-center text-sm font-bold py-2 flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" /> Connection interrupted — reconnecting…
        </div>
      )}
      {tabWarnings > 0 && (
        <div data-testid="anticheat-warning" className="bg-amber-50 border-b border-amber-200 text-amber-800 text-center text-xs font-semibold py-2">
          Warning: Leaving the quiz window has been detected. Tab Closed / Backgrounded: {tabWarnings} time{tabWarnings > 1 ? "s" : ""}.
        </div>
      )}
      <div className="h-1.5 bg-slate-100">
        <div className="h-full transition-[width] duration-100 ease-linear" style={{ width: `${remainPct}%`, background: timerColor }} data-testid="question-timer-bar" />
      </div>
      <div className="max-w-3xl mx-auto px-5 py-8 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: category?.color || "#FF9933" }}>{category?.name || "Category"}</div>
            <div className="font-heading font-bold text-slate-900 mt-0.5" data-testid="question-counter">Question {q.index} / {q.total}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-400">Score</div>
              <div className="font-timer font-bold text-xl text-[#138808]" data-testid="quiz-live-score">{score}</div>
            </div>
            <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center font-timer font-bold text-2xl" style={{ borderColor: timerColor, color: timerColor }} data-testid="question-timer">
              {remainSec}
            </div>
          </div>
        </div>

        <div className="h-1.5 bg-slate-100 rounded-full mb-8 overflow-hidden">
          <div className="h-full tricolor-bar transition-[width] duration-500" style={{ width: `${(q.index / q.total) * 100}%` }} data-testid="quiz-progress-bar" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={q.question_id} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}>
            <h2 className="text-2xl md:text-3xl font-black font-heading text-slate-900 leading-snug tracking-tight" data-testid="question-text">{q.text}</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`answer-option-${LETTERS[i].toLowerCase()}`}
                  disabled={locked}
                  onClick={() => {
                    playSelect();
                    setSelected(i);
                    if (q.auto_submit) setTimeout(() => submitAnswer(i), 250);
                  }}
                  className={`group relative text-left p-5 rounded-xl border-2 font-semibold transition-[transform,border-color,background-color] duration-200 hover:scale-[1.02] disabled:cursor-not-allowed ${
                    selected === i ? "border-[#FF9933] bg-[#FF9933]/10 text-slate-900" : "border-slate-200 bg-white text-slate-700 hover:border-[#003366]/40"
                  }`}
                >
                  <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center font-timer font-bold mr-3 ${selected === i ? "bg-[#FF9933] text-white" : "bg-slate-100 text-slate-500 group-hover:bg-[#003366] group-hover:text-white"} transition-colors`}>
                    {LETTERS[i]}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
            {!q.auto_submit && (
              <button onClick={() => selected !== null && submitAnswer(selected)} disabled={selected === null || locked}
                data-testid="submit-answer-btn"
                className="mt-8 w-full py-4 rounded-xl bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors disabled:opacity-40">
                Lock Answer
              </button>
            )}
          </motion.div>
        </AnimatePresence>
        <p className="mt-10 text-center text-[11px] text-slate-400">Answers are validated and timed by the server. Copy shortcuts are disabled during the quiz.</p>
      </div>
    </div>
  );
}
